import React, { useState, useRef } from 'react';
import FlightSearchForm from '../../flight-search/components/FlightSearchForm';
import FlightOfferCard from '../../flight-search/components/FlightOfferCard';
import Icon from '../../../components/AppIcon';
import { searchFlights, normalizeOffer } from '../../../services/duffelService';
import { parseFlightIntent } from '../../../services/flightIntentService';

const SORT_OPTIONS = [
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
  { value: 'duration_asc', label: 'Duration: Shortest' },
  { value: 'stops_asc', label: 'Stops: Fewest First' },
];

const INTENT_EXAMPLES = [
  'Fly from NYC to London on July 10, 2 adults, business class',
  'Round trip LAX to Tokyo, depart Aug 5 return Aug 20, economy',
  'One way SFO to Paris next Friday, 1 adult',
];

const FlightSearchStep = ({ formData, updateFormData }) => {
  const [loading, setLoading] = useState(false);
  const [offers, setOffers] = useState([]);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [sortBy, setSortBy] = useState('price_asc');
  const [lastSearch, setLastSearch] = useState(null);

  // AI intent state
  const [intentInput, setIntentInput] = useState('');
  const [intentLoading, setIntentLoading] = useState(false);
  const [intentError, setIntentError] = useState(null);
  const [parsedParams, setParsedParams] = useState(null);
  const [showManualForm, setShowManualForm] = useState(false);

  const formRef = useRef(null);
  const selectedOffer = formData?.specs?.selectedFlightOffer || null;

  const runSearch = async (searchParams) => {
    setLoading(true);
    setError(null);
    setOffers([]);
    setHasSearched(true);
    setLastSearch(searchParams);

    try {
      const offerRequest = await searchFlights(searchParams);
      const rawOffers = offerRequest?.offers || [];
      const normalized = rawOffers?.map(normalizeOffer);
      setOffers(normalized);
    } catch (err) {
      console.error('[FlightSearchStep] Search error:', err);
      setError(err?.message || 'Failed to search flights. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Called by the manual FlightSearchForm
  const handleManualSearch = async (searchParams) => {
    await runSearch(searchParams);
  };

  // Called when user submits the AI intent input
  const handleIntentSearch = async (e) => {
    e?.preventDefault();
    if (!intentInput?.trim() || intentLoading) return;

    setIntentLoading(true);
    setIntentError(null);
    setParsedParams(null);

    try {
      const params = await parseFlightIntent(intentInput);
      setParsedParams(params);
      // Immediately trigger the Duffel search with the parsed params
      await runSearch(params);
    } catch (err) {
      console.error('[FlightSearchStep] Intent parse error:', err);
      setIntentError(err?.message || 'Could not understand your request. Please try the manual form below.');
    } finally {
      setIntentLoading(false);
    }
  };

  const sortedOffers = [...offers]?.sort((a, b) => {
    switch (sortBy) {
      case 'price_asc':
        return parseFloat(a?.totalAmount || 0) - parseFloat(b?.totalAmount || 0);
      case 'price_desc':
        return parseFloat(b?.totalAmount || 0) - parseFloat(a?.totalAmount || 0);
      case 'duration_asc': {
        const aDur = a?.slices?.[0]?.duration || '';
        const bDur = b?.slices?.[0]?.duration || '';
        return aDur?.localeCompare(bDur);
      }
      case 'stops_asc':
        return (a?.slices?.[0]?.stops || 0) - (b?.slices?.[0]?.stops || 0);
      default:
        return 0;
    }
  });

  const handleSelectOffer = (offer) => {
    const updatedSpecs = { ...formData?.specs, selectedFlightOffer: offer };
    updateFormData('specs', updatedSpecs);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClearSelection = () => {
    const updatedSpecs = { ...formData?.specs, selectedFlightOffer: null };
    updateFormData('specs', updatedSpecs);
  };

  const isSearching = loading || intentLoading;

  return (
    <div>
      {/* Section Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <div className="h-9 w-9 rounded-xl flex items-center justify-center text-white" style={{ background: 'linear-gradient(to right, #F58A54, #F06F6B, #E0528A)' }}>
            <Icon name="Plane" size={18} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Flight Search</h2>
            <p className="text-sm text-muted-foreground">Search and compare flights powered by Duffel</p>
          </div>
        </div>
      </div>

      {/* Selected Offer Banner */}
      {selectedOffer && (
        <div className="mb-6 p-4 rounded-2xl border border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/30 flex items-center justify-between animate-fade-in">
          <div className="flex items-center gap-3">
            <Icon name="CheckCircle" size={20} className="text-green-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-foreground">
                Flight selected — {selectedOffer?.airline?.name} · {selectedOffer?.totalCurrency} {parseFloat(selectedOffer?.totalAmount)?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-muted-foreground">
                {selectedOffer?.slices?.[0]?.origin} → {selectedOffer?.slices?.[selectedOffer?.slices?.length - 1]?.destination}
              </p>
            </div>
          </div>
          <button
            onClick={handleClearSelection}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors px-3 py-1.5 rounded-lg hover:bg-muted"
          >
            Change
          </button>
        </div>
      )}

      {/* AI Intent Input */}
      <div className="mb-6 bg-card border border-border rounded-2xl p-5 shadow-card">
        <div className="flex items-center gap-2 mb-3">
          <div className="h-6 w-6 rounded-lg flex items-center justify-center text-white flex-shrink-0" style={{ background: 'linear-gradient(to right, #F58A54, #E0528A)' }}>
            <Icon name="Sparkles" size={13} />
          </div>
          <p className="text-sm font-semibold text-foreground">Describe your trip</p>
          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">AI-powered</span>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Tell us where you want to go in plain language — we'll find the right flights automatically.
        </p>

        <form onSubmit={handleIntentSearch} className="flex gap-2">
          <input
            type="text"
            value={intentInput}
            onChange={(e) => {
              setIntentInput(e?.target?.value);
              if (intentError) setIntentError(null);
            }}
            placeholder="e.g. Fly from New York to London on July 10, 2 adults, business class"
            disabled={isSearching}
            className="flex-1 h-10 rounded-xl border border-input bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={!intentInput?.trim() || isSearching}
            className="h-10 px-4 rounded-xl text-white text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 flex-shrink-0"
            style={{ background: 'linear-gradient(to right, #F58A54, #F06F6B, #E0528A)' }}
          >
            {intentLoading ? (
              <>
                <div className="h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                <span>Parsing…</span>
              </>
            ) : (
              <>
                <Icon name="Search" size={15} />
                <span>Search</span>
              </>
            )}
          </button>
        </form>

        {/* Example prompts */}
        {!hasSearched && !intentLoading && (
          <div className="mt-3 flex flex-wrap gap-2">
            {INTENT_EXAMPLES?.map((example) => (
              <button
                key={example}
                type="button"
                onClick={() => setIntentInput(example)}
                className="text-xs text-muted-foreground bg-muted hover:bg-muted/70 px-2.5 py-1 rounded-lg transition-colors"
              >
                {example}
              </button>
            ))}
          </div>
        )}

        {/* Parsed params confirmation */}
        {parsedParams && !intentLoading && (
          <div className="mt-3 p-3 rounded-xl bg-muted/50 border border-border flex flex-wrap gap-x-4 gap-y-1">
            <span className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground">{parsedParams?.origin}</span> → <span className="font-medium text-foreground">{parsedParams?.destination}</span>
            </span>
            <span className="text-xs text-muted-foreground">
              Depart: <span className="font-medium text-foreground">{parsedParams?.departureDate}</span>
            </span>
            {parsedParams?.returnDate && (
              <span className="text-xs text-muted-foreground">
                Return: <span className="font-medium text-foreground">{parsedParams?.returnDate}</span>
              </span>
            )}
            <span className="text-xs text-muted-foreground">
              {parsedParams?.passengers?.adults}A
              {parsedParams?.passengers?.children > 0 ? ` ${parsedParams?.passengers?.children}C` : ''}
              {parsedParams?.passengers?.infants > 0 ? ` ${parsedParams?.passengers?.infants}I` : ''}
              {' · '}<span className="capitalize">{parsedParams?.cabinClass?.replace('_', ' ')}</span>
            </span>
          </div>
        )}

        {/* Intent error */}
        {intentError && (
          <div className="mt-3 flex items-start gap-2 text-destructive">
            <Icon name="AlertCircle" size={15} className="flex-shrink-0 mt-0.5" />
            <p className="text-xs">{intentError}</p>
          </div>
        )}

        {/* Toggle manual form */}
        <button
          type="button"
          onClick={() => setShowManualForm((v) => !v)}
          className="mt-3 text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
        >
          <Icon name={showManualForm ? 'ChevronUp' : 'ChevronDown'} size={13} />
          {showManualForm ? 'Hide manual form' : 'Or fill in details manually'}
        </button>
      </div>

      {/* Manual Search Form (collapsible) */}
      {showManualForm && (
        <div ref={formRef} className="mb-6 animate-fade-in">
          <FlightSearchForm onSearch={handleManualSearch} loading={loading} />
        </div>
      )}

      {/* Loading */}
      {isSearching && (
        <div className="mt-10 flex flex-col items-center gap-4 py-16">
          <div className="h-12 w-12 rounded-full border-4 border-border border-t-transparent animate-spin" style={{ borderTopColor: '#F06F6B' }}></div>
          <p className="text-muted-foreground text-sm">
            {intentLoading ? 'Understanding your travel request…' : 'Searching for the best flights…'}
          </p>
        </div>
      )}

      {/* Search Error */}
      {!isSearching && error && (
        <div className="mt-8 p-5 rounded-2xl border border-destructive/30 bg-destructive/5 flex items-start gap-3">
          <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-destructive">Search failed</p>
            <p className="text-sm text-muted-foreground mt-1">{error}</p>
            {error?.includes('DUFFEL_ACCESS_TOKEN') && (
              <p className="text-xs text-muted-foreground mt-2 bg-muted rounded-lg px-3 py-2">
                To enable flight search, add your Duffel access token as <code className="font-mono">DUFFEL_ACCESS_TOKEN</code> in your Supabase Edge Function secrets.
              </p>
            )}
          </div>
        </div>
      )}

      {/* No results */}
      {!isSearching && hasSearched && !error && offers?.length === 0 && (
        <div className="mt-10 flex flex-col items-center gap-3 py-16 text-center">
          <div className="h-16 w-16 rounded-2xl bg-muted flex items-center justify-center">
            <Icon name="SearchX" size={28} className="text-muted-foreground" />
          </div>
          <p className="text-foreground font-medium">No flights found</p>
          <p className="text-sm text-muted-foreground max-w-sm">
            No offers were returned for this route and date. Try adjusting your search criteria or check that the airport codes are correct.
          </p>
        </div>
      )}

      {/* Results */}
      {!isSearching && offers?.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm font-medium text-foreground">
                {offers?.length} flight{offers?.length !== 1 ? 's' : ''} found
              </p>
              {lastSearch && (
                <p className="text-xs text-muted-foreground">
                  {lastSearch?.origin} → {lastSearch?.destination} · {lastSearch?.departureDate}
                  {lastSearch?.returnDate ? ` → ${lastSearch?.returnDate}` : ''}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-muted-foreground">Sort:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e?.target?.value)}
                className="text-xs border border-input rounded-lg px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {SORT_OPTIONS?.map((opt) => (
                  <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-4">
            {sortedOffers?.map((offer) => (
              <FlightOfferCard
                key={offer?.id}
                offer={offer}
                onSelect={handleSelectOffer}
                isSelected={selectedOffer?.id === offer?.id}
              />
            ))}
          </div>

          <p className="text-xs text-muted-foreground text-center mt-6">
            Prices shown are per person total including taxes. Offers expire — select quickly to lock in your fare.
          </p>
        </div>
      )}
    </div>
  );
};

export default FlightSearchStep;
